import { Platform } from 'react-native'
import { Href, router } from 'expo-router'
import { JsonMap } from '@segment/analytics-react-native'

import { useInitialLinkingStore } from '../../hooks/useInitialLinkingStore'
import { useSessionStore } from '../session/useSessionStore'
import { resolveDeepLink } from './deeplink-resolver'

export * from './deeplink-resolver'

/** Matches `analytics.setEvent` so the store action can be passed straight in. */
type TrackEvent = (event: string, payload?: JsonMap) => void

/**
 * Single auth-gated entry point for every deep link, whatever delivered it —
 * a Customer.io push opened via Linking, a
 * universal/Branch link, or the cold-start launch URL.
 *
 * Unauthenticated: stash the canonical URL so the post-login screens
 * (sign-up, StartPlaying, UserInfo) can replay it. Authenticated: navigate now.
 */
export function handleDeepLink(params: { raw?: string | null; source: string; track: TrackEvent }) {
  const { raw, source, track } = params
  const resolution = resolveDeepLink(raw)

  if (!resolution.ok) {
    // `empty` just means this notification carried no link — not worth an event.
    if (resolution.reason !== 'empty') {
      track('Deep Link Skipped', { url: raw ?? '', reason: resolution.reason, source })
    }
    return
  }

  const { href, url } = resolution.target
  const isAuthenticated = Boolean(useSessionStore.getState().getAccessToken())

  if (!isAuthenticated) {
    track('Deep Link Captured', { url: href, source })
    useInitialLinkingStore.getState().add(url)
    return
  }

  // Web routes deep links through the browser URL bar, not the router.
  if (Platform.OS === 'web') return

  track('Deep Link Consumed', { url: href, source })
  router.navigate(href as Href)
}

/**
 * The onboarding redirect replaces whatever screen a new user was on — usually a deep link
 * that sign-up just replayed (and already cleared). Save that screen again so the end of
 * onboarding (StartPlayingScreen) resumes it instead of dropping the user on Home.
 *
 * `segments` are the route's expo-router segments (`useSegments()`); params that fill a dynamic
 * segment are already in `pathname` and are left out of the query.
 *
 * Returns the saved href, or `undefined` if nothing was saved. A link that is still pending wins
 * over the current screen.
 */
export function stashRouteInterruptedByOnboarding(
  pathname: string,
  params: Record<string, string | string[] | undefined>,
  segments: readonly string[] = []
): string | undefined {
  if (!pathname || pathname === '/' || pathname.startsWith('/onboarding')) return undefined
  if (useInitialLinkingStore.getState().url) return undefined

  const segmentParamKeys = new Set(
    segments.filter((segment) => segment.startsWith('[')).map((segment) => segment.replace(/^\[(\.\.\.)?|\]$/g, ''))
  )
  const query = Object.entries(params)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && !segmentParamKeys.has(entry[0]))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
  const resolution = resolveDeepLink(query ? `${pathname}?${query}` : pathname)
  if (!resolution.ok) return undefined

  useInitialLinkingStore.getState().add(resolution.target.url)
  return resolution.target.href
}
